import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';
import { ImageUpload, GalleryUpload } from '../components/ImageUpload';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

const CATEGORIES = [
  'Gimnasio','CrossFit','Yoga','Pilates','Natación','Boxeo',
  'Artes Marciales','Danza','Bienestar','Fisioterapia',
  'Padel','Fútbol','Atletismo','Ciclismo','Escalada','Otra',
];

export function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<any>({});
  const [gallery, setGallery] = useState<string[]>([]);

  useEffect(() => { if (id) fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const r = await businessApi.getBusiness(id!);
      const b = r.data.data.business;
      setBusiness(b);
      setGallery(Array.isArray(b.gallery) ? b.gallery : []);
      setForm({
        name: b.name || '',
        description: b.description || '',
        address: b.address || '',
        city: b.city || '',
        phone: b.phone || '',
        email: b.email || '',
        website: b.website || '',
        category: b.category || '',
        instagram: b.instagram || '',
        facebook: b.facebook || '',
        tiktok: b.tiktok || '',
        whatsapp: b.whatsapp || '',
      });
    } catch { toast.error('Error cargando el negocio'); }
    finally { setIsLoading(false); }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API_URL}/businesses/${id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Negocio actualizado');
      setEditMode(false);
      fetchData();
    } catch { toast.error('Error al guardar'); }
    finally { setIsSaving(false); }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  if (!business) return (
    <div className="card p-8 text-center"><p className="text-secondary-500">Negocio no encontrado</p></div>
  );

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">{business.name}</h1>
          <p className="text-secondary-500 text-sm mt-1">
            <a href={`https://admin.fitpulse.es/b/${business.slug}`} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
              Ver página pública →
            </a>
          </p>
        </div>
        <button onClick={() => setEditMode(!editMode)} className={editMode ? 'btn-secondary' : 'btn-primary'}>
          {editMode ? 'Cancelar' : '✏️ Editar negocio'}
        </button>
      </div>

      {/* IMÁGENES */}
      <div className="card p-6 space-y-6">
        <h3 className="text-lg font-semibold text-secondary-900">Imágenes</h3>

        {/* Portada */}
        <div>
          <ImageUpload
            type="cover"
            entityId={id!}
            currentImage={business.coverImage}
            label="Imagen de portada (banner del perfil)"
            aspectRatio="16/5"
            onSuccess={url => setBusiness(prev => prev ? { ...prev, coverImage: url } : prev)}
          />
        </div>

        {/* Logo */}
        <div>
          <p className="label mb-2">Logo del negocio</p>
          <ImageUpload
            type="logo"
            entityId={id!}
            currentImage={business.logo}
            aspectRatio="1/1"
            onSuccess={url => setBusiness(prev => prev ? { ...prev, logo: url } : prev)}
          />
        </div>

        {/* Galería */}
        <GalleryUpload
          businessId={id!}
          gallery={gallery}
          onUpdate={setGallery}
        />
      </div>

      {/* INFO / EDICIÓN */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Información del negocio</h3>

        {editMode ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Nombre *</label>
                <input className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
              </div>
              <div>
                <label className="label">Categoría</label>
                <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  <option value="">— Seleccionar —</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="label">Descripción</label>
              <textarea className="input" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Dirección</label>
                <input className="input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </div>
              <div>
                <label className="label">Ciudad</label>
                <input className="input" value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
              </div>
              <div>
                <label className="label">Teléfono</label>
                <input className="input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
              </div>
              <div>
                <label className="label">Email</label>
                <input className="input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              </div>
              <div>
                <label className="label">Web</label>
                <input className="input" value={form.website} onChange={e => setForm({...form, website: e.target.value})} />
              </div>
            </div>

            <hr className="border-secondary-200" />
            <h4 className="font-semibold text-secondary-800">Redes sociales</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">📸 Instagram (usuario)</label>
                <div className="flex"><span className="inline-flex items-center px-3 border border-r-0 border-secondary-300 bg-secondary-50 text-secondary-500 text-sm rounded-l-lg">@</span><input className="input rounded-l-none" value={form.instagram} onChange={e => setForm({...form, instagram: e.target.value})} placeholder="tu_negocio" /></div>
              </div>
              <div>
                <label className="label">🎵 TikTok (usuario)</label>
                <div className="flex"><span className="inline-flex items-center px-3 border border-r-0 border-secondary-300 bg-secondary-50 text-secondary-500 text-sm rounded-l-lg">@</span><input className="input rounded-l-none" value={form.tiktok} onChange={e => setForm({...form, tiktok: e.target.value})} placeholder="tu_negocio" /></div>
              </div>
              <div>
                <label className="label">👥 Facebook (URL)</label>
                <input className="input" value={form.facebook} onChange={e => setForm({...form, facebook: e.target.value})} placeholder="https://facebook.com/..." />
              </div>
              <div>
                <label className="label">💬 WhatsApp (número)</label>
                <input className="input" value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="34600000000" />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditMode(false)} className="btn-secondary">Cancelar</button>
              <button onClick={handleSave} disabled={isSaving} className="btn-primary">
                {isSaving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {business.category && <div><span className="text-xs font-semibold text-secondary-400 uppercase">Categoría</span><p className="text-secondary-700 mt-1">{business.category}</p></div>}
            {business.address && <div><span className="text-xs font-semibold text-secondary-400 uppercase">Dirección</span><p className="text-secondary-700 mt-1">📍 {business.address}</p></div>}
            {business.city && <div><span className="text-xs font-semibold text-secondary-400 uppercase">Ciudad</span><p className="text-secondary-700 mt-1">🏙️ {business.city}</p></div>}
            {business.phone && <div><span className="text-xs font-semibold text-secondary-400 uppercase">Teléfono</span><p className="text-secondary-700 mt-1">📞 {business.phone}</p></div>}
            {business.email && <div><span className="text-xs font-semibold text-secondary-400 uppercase">Email</span><p className="text-secondary-700 mt-1">✉️ {business.email}</p></div>}
            {business.website && <div><span className="text-xs font-semibold text-secondary-400 uppercase">Web</span><p className="mt-1"><a href={business.website} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline text-sm">🌐 {business.website}</a></p></div>}
            {(business as any).instagram && <div><span className="text-xs font-semibold text-secondary-400 uppercase">Instagram</span><p className="text-secondary-700 mt-1">📸 @{(business as any).instagram}</p></div>}
            {(business as any).tiktok && <div><span className="text-xs font-semibold text-secondary-400 uppercase">TikTok</span><p className="text-secondary-700 mt-1">🎵 @{(business as any).tiktok}</p></div>}
            {(business as any).whatsapp && <div><span className="text-xs font-semibold text-secondary-400 uppercase">WhatsApp</span><p className="text-secondary-700 mt-1">💬 {(business as any).whatsapp}</p></div>}
          </div>
        )}
      </div>

      {/* ACCIONES RÁPIDAS */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Gestión rápida</h3>
        <div className="flex flex-wrap gap-3">
          <a href={`/services?businessId=${business.id}`} className="btn-primary">🎯 Servicios</a>
          <a href={`/staff?businessId=${business.id}`} className="btn-secondary">👥 Personal</a>
          <a href={`/availability`} className="btn-secondary">🕐 Horarios</a>
          <a href={`/bookings?businessId=${business.id}`} className="btn-secondary">📅 Reservas</a>
          <a href={`/events?businessId=${business.id}`} className="btn-secondary">🎪 Eventos</a>
          <a href={`https://admin.fitpulse.es/b/${business.slug}`} target="_blank" rel="noreferrer" className="btn-secondary">🌐 Ver perfil público</a>
        </div>
      </div>

    </div>
  );
}
